import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useInfiniteQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { apiClient } from '@/lib/api'
import { subscribe as subscribeFocusBus } from '@/lib/focusBus'
import { describeError } from '@/lib/errors'
import { readApiData, writeApiData } from '@/lib/apiDataCache'
import type { ContactType } from '@/lib/labels'
import type { PeopleFilter } from '@/lib/peopleRole'
import type {
  ApiContact,
  ApiCompany,
  ApiDeal,
  ApiQuote,
  ApiJob,
  ApiInvoice,
  ApiSocialPost,
  ApiWorkflow,
  ApiWorkflowExecutionList,
  ApiWorkflowExecution,
  ApiContentIdea,
  ApiReview,
  ApiNotification,
  ApiPhoneNumber,
  ApiRingGroup,
  ApiBusinessHours,
  DashboardStats,
  PaginatedApiResponse,
  ReviewStats,
  RevenueReport,
  PipelineReport,
  JobsReport,
  InvoiceReport,
  LeadSourcesReport,
  SpendReport,
  ReportRecords,
  ReportRecordMetric,
  InboxThread,
  InboxMessage,
  AttachableResponse,
  UnknownSender,
} from '@/lib/apiTypes'

// ---------------------------------------------------------------------------
// Generic hook
// ---------------------------------------------------------------------------

interface UseApiDataOptions {
  /** How old data must be (ms) before a focus event triggers a refetch. Default 30 s. */
  staleTimeMs?: number
  /** Set true to opt out of focus-refetch entirely (e.g. polling hooks). */
  skipFocusRefetch?: boolean
  /**
   * Keep the last good response in memory under this name (+ the deps), so a
   * page re-opens on it while it refreshes and keeps it when the refresh
   * fails or the device is offline (lib/apiDataCache, UX-018).
   */
  cacheKey?: string
}

export function useApiData<T>(
  fetcher: () => Promise<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps: any[] = [],
  options: UseApiDataOptions = {}
): {
  data: T | null
  loading: boolean
  /** Human message (never "Request failed with status code …"). */
  error: string | null
  /** The original failure, for <QueryState> / isForbiddenError(). */
  rawError: unknown
  refetch: () => void
  /** When `data` was fetched (ms), for "Updated 10:42" markers; null before any data. */
  dataUpdatedAt: number | null
} {
  const { staleTimeMs = 30_000, skipFocusRefetch = false, cacheKey } = options
  const key = cacheKey ? `${cacheKey}:${JSON.stringify(deps)}` : null
  const [data, setData] = useState<T | null>(() => (key ? readApiData<T>(key)?.data ?? null : null))
  const [updatedAt, setUpdatedAt] = useState<number | null>(() => (key ? readApiData<T>(key)?.at ?? null : null))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rawError, setRawError] = useState<unknown>(null)
  const [tick, setTick] = useState(0)
  const lastFetchedAtRef = useRef<number>(0)

  const refetch = useCallback(() => setTick((t) => t + 1), [])

  // Subscribe to the singleton focus bus so returning to the tab (or bfcache
  // restore on iOS Safari) refetches stale data without a full page reload.
  useEffect(() => {
    if (skipFocusRefetch) return
    return subscribeFocusBus(() => {
      if (Date.now() - lastFetchedAtRef.current > staleTimeMs) {
        refetch()
      }
    })
  }, [skipFocusRefetch, staleTimeMs, refetch])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setRawError(null)
    // New deps with a cached answer: show it while this one is in flight.
    const cached = key ? readApiData<T>(key) : null
    if (cached) {
      setData(cached.data)
      setUpdatedAt(cached.at)
    }
    fetcher()
      .then((result) => {
        if (!cancelled) {
          setData(result)
          setLoading(false)
          lastFetchedAtRef.current = Date.now()
          setUpdatedAt(lastFetchedAtRef.current)
          if (key) writeApiData(key, result)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(describeError(err).message)
          setRawError(err)
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  return { data, loading, error, rawError, refetch, dataUpdatedAt: updatedAt }
}

// ---------------------------------------------------------------------------
// Entity params types
// ---------------------------------------------------------------------------

interface ListParams {
  skip?: number
  take?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

interface ContactsParams extends ListParams {
  /** Case-insensitive `tags` membership, filtered server-side. */
  tag?: string
  /** Contact type chip: CUSTOMER / CONTRACTOR / VENDOR / COMPANY (omit for all). */
  type?: ContactType
  /** People chip. `all` still excludes legacy COMPANY rows. */
  role?: PeopleFilter
}

interface DealsParams extends ListParams {
  stage?: string
  /** Exclude WON/LOST server-side — the live pipeline, not a single stage. */
  openOnly?: boolean
  /** Lost view. Comma-separated reason keys. */
  lostReason?: string
  lostFrom?: string
  lostTo?: string
  ownerUserId?: string
  /** `unassigned` = no salesperson. */
  owner?: 'unassigned'
  source?: string
  companyId?: string
}

interface StatusParams extends ListParams {
  status?: string
  // Optional window — Calendar view filters jobs by scheduledStart so the
  // page-size cap doesn't hide old-but-scheduled jobs (qa-sweep tier-1a).
  scheduledFrom?: string
  scheduledTo?: string
}

// ---------------------------------------------------------------------------
// Entity hooks — each unwraps the backend envelope { success, data: { items, total } }
// ---------------------------------------------------------------------------

export function useContacts(params: ContactsParams = {}) {
  const result = useQuery<PaginatedApiResponse<ApiContact>, Error>({
    queryKey: ['contacts', 'list', params],
    queryFn: async () => {
      const res = await apiClient.get('/api/contacts', { params })
      return res.data.data as PaginatedApiResponse<ApiContact>
    },
    staleTime: 30_000,
    /**
     * Hold the previous page across a `take` bump only.
     *
     * `take` is part of the key, so "Load more" starts a brand-new query and
     * `isLoading` flips true — which replaced the whole list with a skeleton
     * and threw away the scroll position on every page bump.
     *
     * But holding it across a *filter* change is worse than the skeleton: the
     * previous filter's rows keep rendering under the newly-selected chip, so
     * for the length of the request the page shows contacts tagged
     * `residential` beneath an active "Commercial" chip and says nothing. A
     * placeholder is only honest when the new query is asking for more of the
     * same question — so it is scoped to the paging case and nothing else.
     */
    placeholderData: (previous, previousQuery) => {
      if (!previous) return undefined
      const prevParams = (previousQuery?.queryKey?.[2] ?? {}) as ContactsParams
      // Every field that changes WHICH rows come back, or in what order.
      // `sortBy`/`sortOrder` were missed first time round: holding the old
      // ordering under a newly-chosen sort is the same lie as holding the old
      // filter's rows, just less obvious. No caller sorts today; the point of
      // scoping a shared hook is the caller that does it next.
      const sameQuestion =
        prevParams.tag === params.tag &&
        prevParams.search === params.search &&
        prevParams.type === params.type &&
        prevParams.role === params.role &&
        prevParams.sortBy === params.sortBy &&
        prevParams.sortOrder === params.sortOrder
      return sameQuestion ? previous : undefined
    },
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    /** True while a background page/filter change is in flight. */
    fetching: result.isFetching,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => { void result.refetch() },
  }
}

/**
 * Paginated contacts for the Contacts list.
 *
 * The list used to page by growing `take` (50 → 100 → 150…). The API caps
 * `take` at 100 in both the Zod and the Fastify querystring schema, so the
 * third "Load more" was a hard 400 — and because the page tests `error`
 * before rows, that replaced the whole list with
 * "Request failed with status code 400". The commit that introduced it was
 * titled "People list silently truncated at 50, with no way to see the rest":
 * it moved the truncation to 100 and turned it into a visible failure.
 *
 * Real offset pagination has no ceiling and asks for one page at a time, so
 * the cap is never approached.
 */
export function useContactsInfinite(params: Omit<ContactsParams, 'skip' | 'take'> & { pageSize?: number } = {}) {
  const pageSize = params.pageSize ?? 50
  const { search, tag, type, role, sortBy, sortOrder } = params
  const result = useInfiniteQuery<PaginatedApiResponse<ApiContact>, Error>({
    queryKey: ['contacts', 'list-infinite', { search, tag, type, role, sortBy, sortOrder, pageSize }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.get('/api/contacts', {
        params: {
          skip: pageParam as number,
          take: pageSize,
          ...(search ? { search } : {}),
          ...(tag ? { tag } : {}),
          ...(type ? { type } : {}),
          ...(role ? { role } : {}),
          ...(sortBy ? { sortBy } : {}),
          ...(sortOrder ? { sortOrder } : {}),
        },
      })
      return res.data.data as PaginatedApiResponse<ApiContact>
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + (p.items?.length ?? 0), 0)
      return loaded < (lastPage.total ?? 0) ? loaded : undefined
    },
    staleTime: 30_000,
  })

  const items = (result.data?.pages ?? []).flatMap((p) => p.items ?? [])
  return {
    items,
    total: result.data?.pages?.[0]?.total ?? 0,
    loading: result.isLoading,
    fetchingMore: result.isFetchingNextPage,
    hasMore: !!result.hasNextPage,
    loadMore: () => { void result.fetchNextPage() },
    error: describeQueryError(result.error),
    rawError: result.error,
    refetch: () => { void result.refetch() },
  }
}

export type ContactTypeCounts = Record<PeopleFilter, number>

/** Counts for Customer / Lead / Sub / Vendor / All. COMPANY is not included. */
export function useContactTypeCounts(search?: string) {
  const result = useQuery<ContactTypeCounts, Error>({
    queryKey: ['contacts', 'type-counts', { search: search || undefined }],
    queryFn: async () => {
      const res = await apiClient.get('/api/contacts/type-counts', { params: search ? { search } : {} })
      return res.data.data as ContactTypeCounts
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })
  return { data: result.data ?? null, loading: result.isLoading }
}

export function useContact(contactId: string | null) {
  const result = useQuery<ApiContact, Error>({
    queryKey: ['contacts', 'detail', contactId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/contacts/${contactId}`)
      return (res.data?.data ?? res.data) as ApiContact
    },
    enabled: !!contactId,
    staleTime: 30_000,
    // No `placeholderData` here, deliberately. On a query keyed by a single
    // id it is backwards: while the id is unchanged the key is unchanged and
    // the value is already cached, so it is never consulted; the only time it
    // IS consulted is when the id just changed — exactly when the previous
    // record must not be shown. It bought nothing and cost a drawer that
    // rendered the last record's numbers under the new one's heading with
    // `loading` false. See useApi.detailPlaceholder.test.tsx.
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    // A missing contact is a state, not an error. Passing axios's raw
    // "Request failed with status code 404" through put a developer string in
    // front of anyone following a stale or deleted link, and it also masked
    // the friendly "Contact not found" panel, which only renders when `error`
    // is null. Everything else still surfaces — preferring the server's own
    // message over axios's generic one.
    error: describeQueryError(result.error),
    rawError: result.error,
    refetch: () => { void result.refetch() },
  }
}

/**
 * Turn a query failure into something worth showing a user, or null when the
 * component has a better state for it (404 → "not found", by default).
 *
 * Delegates to lib/errors `describeError()`: 5xx bodies are never shown
 * verbatim (they can carry connection strings or query text), 429 says what
 * it means, network failures say "You're offline", 403 says whose access it
 * is — never axios's "Request failed with status code …".
 *
 * Prefer `<QueryState query={hook}>` in components; it uses `rawError` to tell
 * forbidden/offline apart.
 */
export function describeQueryError(err: unknown, opts: { notFound?: 'null' | 'message'; action?: string } = {}): string | null {
  if (!err) return null
  const d = describeError(err, { action: opts.action })
  if (d.kind === 'notFound' && (opts.notFound ?? 'null') === 'null') return null
  if (d.kind === 'notFound') return `${d.title}. ${d.message}`
  return d.message
}

export function useCompanies(params: ListParams = {}) {
  return useApiData<PaginatedApiResponse<ApiCompany>>(
    async () => {
      const res = await apiClient.get('/api/companies', { params })
      return res.data.data as PaginatedApiResponse<ApiCompany>
    },
    [params.skip, params.take, params.search, params.sortBy, params.sortOrder],
    { cacheKey: 'companies' }
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Work-entity hooks
//
// All four Work entities (deals/quotes/jobs/invoices) follow the same shape
// PR #418 introduced for contacts:
//   list:   ['<entity>', 'list',   params]
//   detail: ['<entity>', 'detail', id]
//   stats:  ['<entity>', 'stats']
//
// invalidateQueries({queryKey: ['<entity>']}) prefix-matches all three. The
// cross-entity helper `invalidateWorkEntities(queryClient)` invalidates every
// node in the Work pipeline at once — use it for any mutation that can change
// stage assignment (stage advances, job convert-to-invoice, quote sign).
// ─────────────────────────────────────────────────────────────────────────────

export const WORK_QUERY_KEYS = {
  deals: {
    all: ['deals'] as const,
    list: (params: DealsParams) => ['deals', 'list', params] as const,
    detail: (id: string) => ['deals', 'detail', id] as const,
  },
  quotes: {
    all: ['quotes'] as const,
    list: (params: StatusParams) => ['quotes', 'list', params] as const,
    detail: (id: string) => ['quotes', 'detail', id] as const,
  },
  jobs: {
    all: ['jobs'] as const,
    list: (params: StatusParams) => ['jobs', 'list', params] as const,
    detail: (id: string) => ['jobs', 'detail', id] as const,
  },
  invoices: {
    all: ['invoices'] as const,
    list: (params: StatusParams) => ['invoices', 'list', params] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
    stats: ['invoices', 'stats'] as const,
  },
} as const

export function invalidateWorkEntities(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: WORK_QUERY_KEYS.deals.all })
  void queryClient.invalidateQueries({ queryKey: WORK_QUERY_KEYS.quotes.all })
  void queryClient.invalidateQueries({ queryKey: WORK_QUERY_KEYS.jobs.all })
  void queryClient.invalidateQueries({ queryKey: WORK_QUERY_KEYS.invoices.all })
  // Money owed / quotes out / open work (lib/moneyMetrics) move with the records.
  void queryClient.invalidateQueries({ queryKey: ['metrics', 'summary'] })
}

export function useInvalidateWorkEntities(): () => void {
  const queryClient = useQueryClient()
  return useCallback(() => invalidateWorkEntities(queryClient), [queryClient])
}

/** HTTP status behind a query failure, when there was a response at all. */
export function errorStatus(err: unknown): number | null {
  return (err as { response?: { status?: number } })?.response?.status ?? null
}

/**
 * Options for the Work list hooks. `enabled: false` skips the request, e.g.
 * for a role that can't see that list (Crew has jobs, not quotes or invoices),
 * so a 403 on one list never blanks the whole board.
 */
export interface ListQueryOptions {
  enabled?: boolean
}

export function useDeals(params: DealsParams = {}, opts: ListQueryOptions = {}) {
  const result = useQuery<PaginatedApiResponse<ApiDeal>, Error>({
    queryKey: WORK_QUERY_KEYS.deals.list(params),
    queryFn: async () => {
      const res = await apiClient.get('/api/deals', { params })
      return res.data.data as PaginatedApiResponse<ApiDeal>
    },
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    paused: result.fetchStatus === 'paused',
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    status: errorStatus(result.error),
    refetch: () => result.refetch(),
  }
}

export function useDeal(dealId: string | null) {
  const result = useQuery<ApiDeal, Error>({
    queryKey: dealId ? WORK_QUERY_KEYS.deals.detail(dealId) : ['deals', 'detail', null],
    queryFn: async () => {
      const res = await apiClient.get(`/api/deals/${dealId}`)
      return (res.data?.data ?? res.data) as ApiDeal
    },
    enabled: !!dealId,
    staleTime: 30_000,
    // No `placeholderData` here, deliberately. On a query keyed by a single
    // id it is backwards: while the id is unchanged the key is unchanged and
    // the value is already cached, so it is never consulted; the only time it
    // IS consulted is when the id just changed — exactly when the previous
    // record must not be shown. It bought nothing and cost a drawer that
    // rendered the last record's numbers under the new one's heading with
    // `loading` false. See useApi.detailPlaceholder.test.tsx.
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => result.refetch(),
  }
}

export function useQuotes(params: StatusParams = {}, opts: ListQueryOptions = {}) {
  const result = useQuery<PaginatedApiResponse<ApiQuote>, Error>({
    queryKey: WORK_QUERY_KEYS.quotes.list(params),
    queryFn: async () => {
      const res = await apiClient.get('/api/quotes', { params })
      return res.data.data as PaginatedApiResponse<ApiQuote>
    },
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => result.refetch(),
  }
}

export function useQuote(quoteId: string | null) {
  const result = useQuery<ApiQuote, Error>({
    queryKey: quoteId ? WORK_QUERY_KEYS.quotes.detail(quoteId) : ['quotes', 'detail', null],
    queryFn: async () => {
      const res = await apiClient.get(`/api/quotes/${quoteId}`)
      return (res.data?.data ?? res.data) as ApiQuote
    },
    enabled: !!quoteId,
    staleTime: 30_000,
    // No `placeholderData` here, deliberately. On a query keyed by a single
    // id it is backwards: while the id is unchanged the key is unchanged and
    // the value is already cached, so it is never consulted; the only time it
    // IS consulted is when the id just changed — exactly when the previous
    // record must not be shown. It bought nothing and cost a drawer that
    // rendered the last record's numbers under the new one's heading with
    // `loading` false. See useApi.detailPlaceholder.test.tsx.
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => result.refetch(),
  }
}

export function useJobs(params: StatusParams = {}, opts: ListQueryOptions = {}) {
  const result = useQuery<PaginatedApiResponse<ApiJob>, Error>({
    queryKey: WORK_QUERY_KEYS.jobs.list(params),
    queryFn: async () => {
      const res = await apiClient.get('/api/jobs', { params })
      return res.data.data as PaginatedApiResponse<ApiJob>
    },
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => result.refetch(),
  }
}

export function useJob(jobId: string | null) {
  const result = useQuery<ApiJob, Error>({
    queryKey: jobId ? WORK_QUERY_KEYS.jobs.detail(jobId) : ['jobs', 'detail', null],
    queryFn: async () => {
      const res = await apiClient.get(`/api/jobs/${jobId}`)
      return (res.data?.data ?? res.data) as ApiJob
    },
    enabled: !!jobId,
    staleTime: 30_000,
    // No `placeholderData` here, deliberately. On a query keyed by a single
    // id it is backwards: while the id is unchanged the key is unchanged and
    // the value is already cached, so it is never consulted; the only time it
    // IS consulted is when the id just changed — exactly when the previous
    // record must not be shown. It bought nothing and cost a drawer that
    // rendered the last record's numbers under the new one's heading with
    // `loading` false. See useApi.detailPlaceholder.test.tsx.
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => result.refetch(),
  }
}

export function useInvoices(params: StatusParams = {}, opts: ListQueryOptions = {}) {
  const result = useQuery<PaginatedApiResponse<ApiInvoice>, Error>({
    queryKey: WORK_QUERY_KEYS.invoices.list(params),
    queryFn: async () => {
      const res = await apiClient.get('/api/invoices', { params })
      return res.data.data as PaginatedApiResponse<ApiInvoice>
    },
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    status: errorStatus(result.error),
    refetch: () => result.refetch(),
  }
}

export function useInvoice(invoiceId: string | null) {
  const result = useQuery<ApiInvoice, Error>({
    queryKey: invoiceId ? WORK_QUERY_KEYS.invoices.detail(invoiceId) : ['invoices', 'detail', null],
    queryFn: async () => {
      const res = await apiClient.get(`/api/invoices/${invoiceId}`)
      return (res.data?.data ?? res.data) as ApiInvoice
    },
    enabled: !!invoiceId,
    staleTime: 30_000,
    // No `placeholderData` here, deliberately. On a query keyed by a single
    // id it is backwards: while the id is unchanged the key is unchanged and
    // the value is already cached, so it is never consulted; the only time it
    // IS consulted is when the id just changed — exactly when the previous
    // record must not be shown. It bought nothing and cost a drawer that
    // rendered the last record's numbers under the new one's heading with
    // `loading` false. See useApi.detailPlaceholder.test.tsx.
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => result.refetch(),
  }
}

export function useInvoiceStats(opts: ListQueryOptions = {}) {
  const result = useQuery<{ totalPaidRevenue: number }, Error>({
    queryKey: WORK_QUERY_KEYS.invoices.stats,
    queryFn: async () => {
      const res = await apiClient.get('/api/invoices/stats')
      return res.data.data as { totalPaidRevenue: number }
    },
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => result.refetch(),
  }
}

export function useDashboardStats(opts?: { from?: string; to?: string; enabled?: boolean }) {
  const result = useQuery<DashboardStats, Error>({
    queryKey: ['dashboardStats', opts?.from, opts?.to],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (opts?.from) params.from = opts.from
      if (opts?.to) params.to = opts.to
      const res = await apiClient.get('/api/dashboard/stats', { params })
      return res.data.data as DashboardStats
    },
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    // Callers that ignore this render the zero-valued fallback instead —
    // "$0.00" and "0 deals" on a request that never came back.
    error: describeQueryError(result.error),
    rawError: result.error,
    refetch: () => { void result.refetch() },
  }
}

// ---------------------------------------------------------------------------
// Social Media hooks
// ---------------------------------------------------------------------------

interface SocialPostsParams extends ListParams {
  platform?: string
  status?: string
}

export function useSocialPosts(params: SocialPostsParams = {}) {
  return useApiData<PaginatedApiResponse<ApiSocialPost>>(
    async () => {
      const res = await apiClient.get('/api/social/posts', { params })
      return res.data.data as PaginatedApiResponse<ApiSocialPost>
    },
    [params.skip, params.take, params.search, params.platform, params.status, params.sortBy, params.sortOrder],
    { cacheKey: 'socialPosts' }
  )
}

export function useContentIdeas(params: ListParams = {}) {
  return useApiData<PaginatedApiResponse<ApiContentIdea>>(
    async () => {
      const res = await apiClient.get('/api/social/ideas', { params })
      return res.data.data as PaginatedApiResponse<ApiContentIdea>
    },
    [params.skip, params.take],
    { cacheKey: 'contentIdeas' }
  )
}

// ---------------------------------------------------------------------------
// Reviews hooks
// ---------------------------------------------------------------------------

interface ReviewsParams extends ListParams {
  platform?: string
  status?: string
  rating?: number
}

export function useReviews(params: ReviewsParams = {}) {
  return useApiData<PaginatedApiResponse<ApiReview>>(
    async () => {
      const res = await apiClient.get('/api/reviews', { params })
      return res.data.data as PaginatedApiResponse<ApiReview>
    },
    [params.skip, params.take, params.platform, params.status, params.rating, params.sortBy, params.sortOrder],
    { cacheKey: 'reviews' }
  )
}

export function useReviewStats() {
  return useApiData<ReviewStats>(async () => {
    const res = await apiClient.get('/api/reviews/stats')
    return res.data.data as ReviewStats
  }, [], { cacheKey: 'reviewStats' })
}

// ---------------------------------------------------------------------------
// Reports hooks
// ---------------------------------------------------------------------------

interface ReportParams {
  range?: string
  startDate?: string
  endDate?: string
}

export function useRevenueReport(params: ReportParams = {}) {
  return useApiData<RevenueReport>(
    async () => {
      const res = await apiClient.get('/api/reports/revenue', { params })
      return res.data.data as RevenueReport
    },
    [params.range, params.startDate, params.endDate],
    { cacheKey: 'reportRevenue' }
  )
}

export function usePipelineReport(opts: { previewEmpty?: boolean } = {}) {
  const previewEmpty = opts.previewEmpty === true
  return useApiData<PipelineReport>(async () => {
    // Client-side on purpose: a Pages preview of this branch still talks to
    // the current API, which may have live opportunities. The flag never
    // writes, and it does not invent a non-zero funnel.
    if (previewEmpty) {
      const { emptyPipelinePreview } = await import('@/lib/emptyPipeline')
      return emptyPipelinePreview()
    }
    const res = await apiClient.get('/api/reports/pipeline')
    return res.data.data as PipelineReport
  }, [previewEmpty], { cacheKey: 'reportPipeline' })
}

export function useJobsReport(params: ReportParams = {}) {
  return useApiData<JobsReport>(
    async () => {
      const res = await apiClient.get('/api/reports/jobs', { params })
      return res.data.data as JobsReport
    },
    [params.range, params.startDate, params.endDate],
    { cacheKey: 'reportJobs' }
  )
}

export function useInvoiceReport() {
  return useApiData<InvoiceReport>(async () => {
    const res = await apiClient.get('/api/reports/invoices')
    return res.data.data as InvoiceReport
  }, [], { cacheKey: 'reportInvoices' })
}

/** Synced Google Ads / Meta Ads spend for the same period windows as Sources. */
export function useSpendReport(params: ReportParams = {}) {
  return useApiData<SpendReport>(
    async () => {
      const res = await apiClient.get('/api/reports/spend', { params })
      return res.data.data as SpendReport
    },
    [params.range, params.startDate, params.endDate],
    { cacheKey: 'reportSpend' },
  )
}

/** First-touch Sources breakdown. `range` filters sources rows; legacy bySource stays all-time on the server. */
export function useLeadsReport(params: ReportParams = {}) {
  return useApiData<LeadSourcesReport>(
    async () => {
      const res = await apiClient.get('/api/reports/leads', { params })
      return res.data.data as LeadSourcesReport
    },
    [params.range, params.startDate, params.endDate],
    { cacheKey: 'reportLeads' },
  )
}

export interface ReportRecordsParams extends ReportParams {
  metric: ReportRecordMetric
  segment?: string
  service?: string
  limit?: number
}

/** The invoices / opportunities / jobs behind a report figure (drill-down + export). */
export async function fetchReportRecords(params: ReportRecordsParams): Promise<ReportRecords> {
  const res = await apiClient.get('/api/reports/records', { params })
  return res.data.data as ReportRecords
}

export function useReportRecords(params: ReportRecordsParams | null) {
  return useApiData<ReportRecords | null>(
    async () => (params ? fetchReportRecords(params) : null),
    [params?.metric, params?.segment, params?.service, params?.range, params?.startDate, params?.endDate, params?.limit],
    { skipFocusRefetch: true },
  )
}

// ---------------------------------------------------------------------------
// Workflow hooks
// ---------------------------------------------------------------------------

export function useWorkflows(params: ListParams = {}) {
  return useApiData<PaginatedApiResponse<ApiWorkflow>>(
    async () => {
      const res = await apiClient.get('/api/workflows', { params })
      return res.data.data as PaginatedApiResponse<ApiWorkflow>
    },
    [params.skip, params.take, params.search, params.sortBy, params.sortOrder],
    { cacheKey: 'workflows' }
  )
}

// Polls every 5 s while any execution is running or paused
export function useWorkflowExecutions(
  workflowId: string | null,
  params: { skip?: number; take?: number } = {}
) {
  const result = useQuery<ApiWorkflowExecutionList, Error>({
    queryKey: ['workflowExecutions', workflowId, params],
    queryFn: async () => {
      const res = await apiClient.get(`/api/workflows/${workflowId}/executions`, { params })
      return res.data.data as ApiWorkflowExecutionList
    },
    enabled: !!workflowId,
    refetchInterval: (query) => {
      const data = query.state.data as ApiWorkflowExecutionList | undefined
      const hasLive = data?.items?.some((e) => e.status === 'running' || e.status === 'paused') ?? false
      return hasLive ? 5000 : false
    },
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => { void result.refetch() },
  }
}

// Polls every 2 s while execution is running or paused, stops on terminal status
export function useWorkflowExecution(execId: string | null) {
  const result = useQuery<ApiWorkflowExecution, Error>({
    queryKey: ['workflowExecution', execId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/workflows/executions/${execId}`)
      return (res.data?.data ?? res.data) as ApiWorkflowExecution
    },
    enabled: !!execId,
    refetchInterval: (query) => {
      const data = query.state.data as ApiWorkflowExecution | undefined
      const isLive = data?.status === 'running' || data?.status === 'paused'
      return isLive ? 2000 : false
    },
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => { void result.refetch() },
  }
}

// ---------------------------------------------------------------------------
// Phone hooks
// ---------------------------------------------------------------------------

export function usePhoneNumbers() {
  return useApiData<ApiPhoneNumber[]>(async () => {
    const res = await apiClient.get('/api/phone/numbers')
    return res.data.data as ApiPhoneNumber[]
  }, [], { cacheKey: 'phoneNumbers' })
}

export function useRingGroups() {
  return useApiData<ApiRingGroup[]>(async () => {
    const res = await apiClient.get('/api/phone/ring-groups')
    return res.data.data as ApiRingGroup[]
  }, [], { cacheKey: 'ringGroups' })
}

export function useBusinessHours() {
  return useApiData<ApiBusinessHours>(async () => {
    const res = await apiClient.get('/api/phone/business-hours')
    return res.data.data as ApiBusinessHours
  }, [], { cacheKey: 'businessHours' })
}

// ---------------------------------------------------------------------------
// Notification hooks
// ---------------------------------------------------------------------------

export function useNotifications() {
  return useApiData<{ items: ApiNotification[]; total: number }>(
    async () => {
      const res = await apiClient.get('/api/notifications/recent')
      return res.data.data as { items: ApiNotification[]; total: number }
    },
    [],
    { cacheKey: 'recentActivity' }
  )
}

// ---------------------------------------------------------------------------
// Inbox hooks
// ---------------------------------------------------------------------------

// Scoped retry policy for inbox queries: if the session expires mid-tab
// (token TTL, server-side revoke), the global axios interceptor already
// triggers the /login redirect — but TanStack Query's default 3-retry policy
// would otherwise hammer the endpoint while the redirect navigates. Skip
// retry on 401 entirely so the redirect handler can run unimpeded. Bound
// the retry to ONE attempt for transient 5xx/network so a flapping connection
// doesn't multiply badge polls. Scoped to inbox to avoid cascading effects
// on Work tab and other surfaces (per scope note in FOR Wave 3).
function inboxRetry(failureCount: number, error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    if (status === 401 || status === 403) return false
    if (status && status >= 400 && status < 500) return false
  }
  return failureCount < 1
}

export interface InboxThreadsFilters {
  channel?: 'all' | 'email' | 'sms'
  status?: 'all' | 'unread' | 'needs_reply'
  /** Server-side search across every thread (name, email, phone, subject, body). */
  search?: string
  pageSize?: number
}

interface InboxThreadsPage {
  items: InboxThread[]
  total: number
  unreadCount: number
  needsReplyCount: number
  nextCursor: string | null
  unknownSenders: UnknownSender[]
}

/**
 * Conversation list with real paging. The server groups, filters and
 * searches every thread and hands back a keyset cursor, so older
 * conversations are reachable by scrolling and search isn't limited to what
 * happens to be loaded (UX-082).
 */
export function useInboxThreads(filters: InboxThreadsFilters = {}) {
  const channel = filters.channel ?? 'all'
  const status = filters.status ?? 'all'
  const search = (filters.search ?? '').trim()
  const pageSize = filters.pageSize ?? 25
  const result = useInfiniteQuery<InboxThreadsPage, Error>({
    queryKey: ['inboxThreads', { channel, status, search, pageSize }],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number> = { take: pageSize }
      if (channel !== 'all') params.channel = channel
      if (status !== 'all') params.status = status
      if (search) params.search = search
      if (pageParam) params.cursor = pageParam as string
      const res = await apiClient.get('/api/inbox/threads', { params })
      const data = res.data.data as Partial<InboxThreadsPage> & { items: InboxThread[]; total: number }
      return {
        items: data.items ?? [],
        total: data.total ?? 0,
        unreadCount: data.unreadCount ?? 0,
        needsReplyCount: data.needsReplyCount ?? 0,
        nextCursor: data.nextCursor ?? null,
        unknownSenders: data.unknownSenders ?? [],
      }
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 10_000,
    retry: inboxRetry,
    // Keep the old list on screen while a new search/filter loads, instead of
    // flashing skeletons on every keystroke.
    placeholderData: (prev) => prev,
  })
  const pages = result.data?.pages ?? []
  const first = pages[0]
  return {
    items: pages.flatMap((p) => p.items),
    unknownSenders: first?.unknownSenders ?? [],
    total: first?.total ?? 0,
    unreadCount: first?.unreadCount ?? 0,
    needsReplyCount: first?.needsReplyCount ?? 0,
    hasData: !!result.data,
    loading: result.isLoading,
    /** A new search/filter is loading while the previous results stay visible. */
    refreshing: result.isFetching && !result.isFetchingNextPage && !result.isLoading,
    fetchingMore: result.isFetchingNextPage,
    hasMore: !!result.hasNextPage,
    loadMore: () => { if (result.hasNextPage && !result.isFetchingNextPage) void result.fetchNextPage() },
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => { void result.refetch() },
  }
}

export interface InboxChannelStatus {
  email: { available: boolean; reason: string | null }
  sms: { available: boolean; reason: string | null }
}

/**
 * Whether email / texting can actually send right now (same checks as
 * POST /inbox/send). Null while loading - callers must not warn until they
 * know (UX-037).
 */
export function useInboxChannels() {
  const result = useQuery<InboxChannelStatus, Error>({
    queryKey: ['inboxChannels'],
    queryFn: async () => {
      const res = await apiClient.get('/api/inbox/channels')
      return res.data.data as InboxChannelStatus
    },
    staleTime: 60_000,
    retry: inboxRetry,
  })
  return { data: result.data ?? null, loading: result.isLoading }
}

export function useInboxStats() {
  const result = useQuery<{ unreadCount: number }, Error>({
    queryKey: ['inboxStats'],
    queryFn: async () => {
      const res = await apiClient.get('/api/inbox/threads', { params: { take: 1 } })
      const data = res.data.data as { unreadCount: number }
      return { unreadCount: data.unreadCount ?? 0 }
    },
    staleTime: 10_000,
    retry: inboxRetry,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => { void result.refetch() },
  }
}

export function useInboxThread(contactId: string | null) {
  const result = useQuery<{ contact: Record<string, unknown>; messages: InboxMessage[] }, Error>({
    queryKey: ['inboxThread', contactId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/inbox/threads/${contactId}`)
      return res.data.data as { contact: Record<string, unknown>; messages: InboxMessage[] }
    },
    enabled: !!contactId,
    staleTime: 10_000,
    retry: inboxRetry,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => { void result.refetch() },
  }
}

export function useContactAttachable(contactId: string | null) {
  const result = useQuery<AttachableResponse, Error>({
    queryKey: ['contactAttachable', contactId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/inbox/contacts/${contactId}/attachable`)
      return res.data.data as AttachableResponse
    },
    enabled: !!contactId,
    staleTime: 30_000,
    retry: inboxRetry,
  })
  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: describeQueryError(result.error, { notFound: 'message' }),
    rawError: result.error,
    refetch: () => { void result.refetch() },
  }
}
